import React, { useCallback, useContext, useState } from 'react';
import { View, Image, Platform } from 'react-native';
import { Card, Icon, Input, Text, Tab } from '@rneui/themed';
import { Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import MultipleCardIcons from './MultipleCardIcons';
import { H3 } from '../../text';
import Utils from '../../../common/Utils';
import AppContext from '../../../contexts/App';
import GivingContext, { ActionTypes, PaymentMethods } from '../../../contexts/Giving';
import useAxios from '../../../hooks/useAxios';
import Styles from './styles';

const PaymentMethod = props => {
    const { postGeneric } = useAxios();
    const { state: appState } = useContext(AppContext);
    const { state, dispatch } = useContext(GivingContext);
    const [index, setIndex] = useState(0);
    const [expirySelection, setExpirySelection] = useState({ start: 0, end: 0 });
    const clearErrors = props?.clearErrors;
    const control = props?.control;
    const errors = props?.errors;
    const getValues = props?.getValues;
    const width = props?.width;

    const tokenize = async (val, type) => {
        if (type === PaymentMethods.CREDIT) {
            val = getValues('cardNumber');
        } else if (type === PaymentMethods.ACH) {
            const routing = getValues('rountingNumber');
            const account = getValues('accountNumber');

            val = routing + account;
        }

        if (state.cartDetails.paymentInfo?.method === type && state.cartDetails.paymentInfo?.token && state.cartDetails.paymentInfo?.last4?.equals(val.slice(-4))) {
            return;
        }

        if (val.length >= 15) {
            const env = appState.settings?.websiteSettings?.environment;
            const tokenizeEndpoint =
                env?.equals('Development') || env?.equals('Staging')
                    ? appState.settings?.cardConnectSettings?.test?.tokenizeEndpoint
                    : appState.settings?.cardConnectSettings?.us?.tokenizeEndpoint;

            const tokenResponse = await postGeneric(tokenizeEndpoint, { account: val }, 'application/json');

            if (tokenResponse?.errorcode === 0 && tokenResponse?.token) {
                const paymentInfo = {
                    last4: val.slice(-4),
                    token: tokenResponse.token,
                    method: type
                };

                if (type === PaymentMethods.CREDIT) {
                    if (Utils.ValidAmexCardRegExp.test(val)) {
                        paymentInfo.ccType = 'cc-amex';
                    } else if (Utils.ValidMasterCardRegExp.test(val)) {
                        paymentInfo.ccType = 'cc-mastercard';
                    } else if (Utils.ValidDiscoverCardRegExp.test(val)) {
                        paymentInfo.ccType = 'cc-discover';
                    } else if (Utils.ValidVisaCardRegExp.test(val)) {
                        paymentInfo.ccType = 'cc-visa';
                    }
                }

                dispatch({
                    type: ActionTypes.UPDATE_CART_DETAILS,
                    data: { paymentInfo }
                });

                return true;
            }

            return 'Failed to verify credit card number. Please try again.';
        }
    };

    const verifyMonthYear = useCallback(
        value => {
            if (value.length === 0) {
                return true;
            }

            value = value.replace(/\D/g, '');

            const month = value.substring(0, 2);
            const year = parseInt(value.substring(2, value.length));
            const creditCardDate = new Date(year, month);
            const today = new Date();

            if (year - today.getFullYear() > 10) {
                return 'Expiration year inalid.';
            }

            if (value.length >= 6) {
                if (creditCardDate < today) {
                    return 'Expiration is prior to today.';
                }

                clearErrors('monthYear');
                return true;
            } else {
                return 'Expiration invalid.';
            }
        },
        [clearErrors]
    );

    return (
        <Card containerStyle={Styles.PaymentMethod.container} wrapperStyle={{ width: '100%' }}>
            <Tab
                disableIndicator={true}
                value={index}
                onChange={e => {
                    dispatch({
                        type: ActionTypes.UPDATE_CART_DETAILS,
                        data: {
                            paymentInfo: {
                                method: e === 0 ? PaymentMethods.CREDIT : PaymentMethods.ACH
                            }
                        }
                    });
                    setIndex(e);
                }}
            >
                <Tab.Item
                    containerStyle={{
                        borderBottomColor: index === 0 ? 'rgb(161, 180, 198)' : undefined,
                        borderBottomWidth: index === 0 ? 3 : undefined
                    }}
                    focusable={false}
                >
                    <View
                        style={{
                            alignItems: 'center',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            marginHorizontal: 30,
                            marginVertical: 15
                        }}
                    >
                        <Icon name='credit-card' size={width <= 480 ? 15 : 28} type='font-awesome-5' containerStyle={{ overflow: undefined }} focusable={false} />
                        <H3 style={Styles.PaymentMethod.headingText} text='CREDIT/DEBIT' />
                    </View>
                </Tab.Item>
                <Tab.Item
                    containerStyle={{
                        borderBottomColor: index === 1 ? 'rgb(161, 180, 198)' : undefined,
                        borderBottomWidth: index === 1 ? 3 : undefined
                    }}
                    focusable={false}
                >
                    <View
                        style={{
                            alignItems: 'center',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            marginHorizontal: 30,
                            marginVertical: 15
                        }}
                    >
                        <Icon name='bank' size={width <= 480 ? 15 : 28} type='font-awesome' containerStyle={{ overflow: undefined }} focusable={false} />
                        <H3 style={Styles.PaymentMethod.headingText} text='Bank Account' />
                    </View>
                </Tab.Item>
            </Tab>
            {/* Credit/Debit */}
            {index === 0 && (
                <View style={Styles.PaymentMethod.inputContainer}>
                    <View style={Styles.PaymentMethod.inputWrapper}>
                        <View
                            style={{
                                alignItems: 'center',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                width: '100%'
                            }}
                        >
                            <Controller
                                control={control}
                                defaultValue=''
                                name='cardNumber'
                                render={({ field, fieldState }) => {
                                    const enforce = value => {
                                        return value.replace(/\D/g, '');
                                    };

                                    field.value = enforce(field.value);
                                    return (
                                        <Input
                                            {...field}
                                            containerStyle={{ width: '66%', paddingHorizontal: 5 }}
                                            errorMessage={
                                                !fieldState.error ? (
                                                    <Text style={Styles.Form.label}>
                                                        Card Number
                                                        <Text style={Styles.Form.asterisk}>*</Text>
                                                    </Text>
                                                ) : (
                                                    errors?.cardNumber?.message
                                                )
                                            }
                                            errorStyle={!fieldState.error ? Styles.Form.placeholderStyle : Styles.Form.errorStyle}
                                            inputMode='numeric'
                                            inputStyle={[
                                                Styles.Form.inputStyle,
                                                {
                                                    borderColor: 'white',
                                                    borderBottomColor: fieldState.isTouched && fieldState.invalid ? '#fe0000' : '#333333',
                                                    borderWidth: 1
                                                }
                                            ]}
                                            inputRef={field.ref}
                                            keyboardType='number-pad'
                                            maxLength={16}
                                            onBlur={field.onBlur}
                                            spellCheck={false}
                                            textContentType='creditCardNumber'
                                        />
                                    );
                                }}
                                rules={{
                                    maxLength: 16,
                                    required: {
                                        message: 'Credit card number is required',
                                        value: true
                                    },
                                    pattern: {
                                        message: 'Not a valid credit card number',
                                        value: Utils.ValidCreditCardRegExp
                                    },
                                    validate: async val => await tokenize(val, PaymentMethods.CREDIT)
                                }}
                            />
                            <MultipleCardIcons />
                        </View>
                        <View style={{ flexDirection: 'row', marginTop: '10pt', width: '100%' }}>
                            <Controller
                                control={control}
                                defaultValue=''
                                name='monthYear'
                                render={({ field, fieldState }) => {
                                    const enforce = val => {
                                        if (val?.length > 0) {
                                            val = val.replace(/\D/g, '');

                                            const month = val.substring(0, 2);
                                            const year = val.substring(2, val.length);

                                            if (val.length === 2) {
                                                const monthInt = parseInt(month);

                                                if (monthInt < 1 || monthInt > 12) {
                                                    return val;
                                                }

                                                return `${month}/`;
                                            } else if (val.length >= 3) {
                                                return `${month}/${year}`;
                                            } else {
                                                return val;
                                            }
                                        }
                                    };

                                    field.value = enforce(field.value);

                                    return (
                                        <Input
                                            {...field}
                                            containerStyle={{ width: '50%', minHeight: 20 }}
                                            errorMessage={
                                                !fieldState.error ? (
                                                    <Text style={Styles.Form.label}>
                                                        MM/YYYY
                                                        <Text style={Styles.Form.asterisk}>*</Text>
                                                    </Text>
                                                ) : (
                                                    errors?.monthYear?.message
                                                )
                                            }
                                            errorStyle={!fieldState.error ? Styles.Form.placeholderStyle : Styles.Form.errorStyle}
                                            innerRef={field.ref}
                                            inputStyle={[
                                                Styles.Form.inputStyle,
                                                {
                                                    borderColor: 'white',
                                                    borderBottomColor: fieldState.isTouched && fieldState.invalid ? '#fe0000' : '#333333',
                                                    borderWidth: 1
                                                }
                                            ]}
                                            maxLength={7}
                                            onBlur={field.onBlur}
                                            onKeyPress={({ nativeEvent: { key: keyValue } }) => {
                                                if (keyValue === 'Backspace') {
                                                    if (field.value.length === 3) {
                                                        setExpirySelection({ start: 2, end: 2 });
                                                    }
                                                }
                                            }}
                                            onSelectionChange={({
                                                nativeEvent: {
                                                    selection: { start, end }
                                                }
                                            }) => {
                                                setExpirySelection({ start, end });
                                            }}
                                            placeholder='MM/YYYY'
                                            placeholderTextColor='#8897a2'
                                            selection={expirySelection}
                                            spellCheck={false}
                                        />
                                    );
                                }}
                                rules={{
                                    required: {
                                        message: 'Month/Year is required',
                                        value: true
                                    },
                                    pattern: {
                                        message: 'Invalid expiration date',
                                        value: Utils.ValidDateFormat
                                    },
                                    validate: value => verifyMonthYear(value)
                                }}
                            />
                            <Controller
                                control={control}
                                defaultValue=''
                                name='verifyCode'
                                render={({ field, fieldState }) => {
                                    const enforce = value => {
                                        return value.replace(/\D/g, '');
                                    };

                                    field.value = enforce(field.value);
                                    return (
                                        <Input
                                            {...field}
                                            containerStyle={{ width: '50%' }}
                                            errorMessage={
                                                !fieldState.error ? (
                                                    <Text style={Styles.Form.label}>
                                                        CVV
                                                        <Text style={Styles.Form.asterisk}>*</Text>
                                                    </Text>
                                                ) : (
                                                    errors?.verifyCode?.message
                                                )
                                            }
                                            errorStyle={!fieldState.error ? Styles.Form.placeholderStyle : Styles.Form.errorStyle}
                                            innerRef={field.ref}
                                            inputMode='numeric'
                                            inputStyle={{
                                                ...Styles.Form.inputStyle,
                                                borderColor: 'white',
                                                borderBottomColor: fieldState.isTouched && fieldState.invalid ? '#fe0000' : '#333333',
                                                borderWidth: 1
                                            }}
                                            keyboardType='number-pad'
                                            maxLength={state.cartDetails?.paymentInfo?.ccType?.equals('cc-amex') ? 4 : 3}
                                            onBlur={field.onBlur}
                                            spellCheck={false}
                                        />
                                    );
                                }}
                                rules={{
                                    maxLength: state.cartDetails?.paymentInfo?.ccType?.equals('cc-amex') ? 4 : 3,
                                    pattern: {
                                        message: 'Amount must be a numeric value.',
                                        value: Utils.ValidCVVRegExp
                                    },
                                    required: {
                                        message: 'CVV is required',
                                        value: true
                                    }
                                }}
                            />
                        </View>
                    </View>
                </View>
            )}
            {/* ACH */}
            {index === 1 && (
                <View style={Styles.PaymentMethod.inputContainer}>
                    <View
                        style={{
                            ...Styles.PaymentMethod.inputWrapper,
                            paddingBottom: undefined
                        }}
                    >
                        <View style={{ top: 20, right: 20, position: 'absolute' }}>
                            <Icon type='ionicon' size={width <= 480 ? 15 : 28} name='shield-checkmark-outline' containerStyle={{ overflow: undefined }} />
                        </View>
                        <View style={{ flexDirection: 'column', width: '100%' }}>
                            <View style={{ flexDirection: 'row', width: '100%' }}>
                                <Controller
                                    control={control}
                                    defaultValue=''
                                    name='rountingNumber'
                                    render={({ field, fieldState }) => {
                                        const enforce = value => {
                                            return value.replace(/\D/g, '');
                                        };

                                        field.value = enforce(field.value);
                                        return (
                                            <Input
                                                {...field}
                                                containerStyle={{ width: '50%' }}
                                                errorMessage={
                                                    !fieldState.error ? (
                                                        <Text style={Styles.Form.label}>
                                                            Routing Number
                                                            <Text style={Styles.Form.asterisk}>*</Text>
                                                        </Text>
                                                    ) : (
                                                        errors?.rountingNumber?.message
                                                    )
                                                }
                                                errorStyle={!fieldState.error ? Styles.Form.placeholderStyle : Styles.Form.errorStyle}
                                                innerRef={field.ref}
                                                inputMode='numeric'
                                                inputStyle={{
                                                    ...Styles.Form.inputStyle,
                                                    borderColor: 'white',
                                                    borderBottomColor: fieldState.isTouched && fieldState.invalid ? '#fe0000' : '#333333',
                                                    borderWidth: 1
                                                }}
                                                keyboardType='number-pad'
                                                maxLength={9}
                                                onBlur={field.onBlur}
                                                spellCheck={false}
                                            />
                                        );
                                    }}
                                    rules={{
                                        required: {
                                            message: 'Routing Number is required',
                                            value: true
                                        },
                                        pattern: {
                                            message: 'Invalid Routing Number',
                                            value: Utils.ValidRoutingNumberExp
                                        },
                                        validate: async val => tokenize(val, PaymentMethods.ACH)
                                    }}
                                />

                                <Controller
                                    control={control}
                                    defaultValue=''
                                    name='accountNumber'
                                    render={({ field, fieldState }) => {
                                        const enforce = value => {
                                            return value.replace(/\D/g, '');
                                        };

                                        field.value = enforce(field.value);
                                        return (
                                            <Input
                                                {...field}
                                                containerStyle={{ width: '50%' }}
                                                errorMessage={
                                                    !fieldState.error ? (
                                                        <Text style={Styles.Form.label}>
                                                            Account Number
                                                            <Text style={Styles.Form.asterisk}>*</Text>
                                                        </Text>
                                                    ) : (
                                                        errors?.accountNumber?.message
                                                    )
                                                }
                                                errorStyle={!fieldState.error ? Styles.Form.placeholderStyle : Styles.Form.errorStyle}
                                                innerRef={field.ref}
                                                inputMode='numeric'
                                                inputStyle={{
                                                    ...Styles.Form.inputStyle,
                                                    borderColor: 'white',
                                                    borderBottomColor: fieldState.isTouched && fieldState.invalid ? '#fe0000' : '#333333',
                                                    borderWidth: 1
                                                }}
                                                keyboardType='number-pad'
                                                maxLength={12}
                                                onBlur={field.onBlur}
                                                spellCheck={false}
                                            />
                                        );
                                    }}
                                    rules={{
                                        maxLength: 12,
                                        pattern: {
                                            message: 'Invalid Account Number',
                                            value: Utils.ValidAccountNumberExp
                                        },
                                        required: {
                                            message: 'Account Number is required',
                                            value: true
                                        },
                                        validate: async val => tokenize(val, PaymentMethods.ACH)
                                    }}
                                />
                            </View>
                            <Image
                                resizeMode='contain'
                                style={{ height: 200, marginVertical: Platform.OS === 'web' ? '5pt' : 5, width: '100%' }}
                                source={require('./routing-bank-illustration.svg')}
                            />
                        </View>
                    </View>
                </View>
            )}
        </Card>
    );
};

PaymentMethod.propTypes = {
    clearErrors: PropTypes.func.isRequired,
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    getValues: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired
};

export default PaymentMethod;
